import { useTranslation } from 'react-i18next';
import type { ChangeSummary } from '../../hooks/useYearPlan';
import WarningIcon from '../../assets/icons/warning.svg?react';
import { Modal } from '../UI';
import { Button } from '../UI/base/Button';
import toolbarStyles from './GanttEditToolbar.module.css';
import cardStyles from './ReviewChangesModal.module.css';

interface ReviewChangesModalProps {
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  changeSummary?: ChangeSummary | null;
}

function formatDelta(value: number): string {
  if (value > 0) return `+${String(value)}`;
  return String(value);
}

// A change measured against nothing has no percentage. Guarding the division by
// falling back to 0 rendered growth from an empty year plan as "+0%" — the one
// reading that is never true.
//
// Unrounded on purpose. Math.round(-0.4) is -0, which compares `>= 0`, so rounding
// here would hand a loss of a fraction of a percent to the "increase" branch below
// and paint it green. Rounding belongs at display time, where the sign is already
// settled.
function percentChange(delta: number, baseline: number): number | null {
  if (baseline <= 0) return null;
  return (delta / baseline) * 100;
}

// N/A shares the chip an increase gets; there is no separate "no baseline" style.
function getFootnoteClass(percent: number | null): string {
  if (percent === null || percent >= 0) return cardStyles.footnotePositive;
  if (Math.abs(percent) <= 10) return cardStyles.footnoteWarning;
  return cardStyles.footnoteDanger;
}

// A change too small to reach half a percent gets no sign: "+0%" and "-0%" are
// both noise, and the delta above the footnote already says which way it went.
function formatPercent(percent: number): string {
  const rounded = Math.round(Math.abs(percent));
  if (rounded === 0) return '0%';
  return `${percent > 0 ? '+' : '-'}${String(rounded)}%`;
}

export const ReviewChangesModal = ({
  onClose,
  onConfirm,
  changeSummary
}: ReviewChangesModalProps) => {
  const { t } = useTranslation();

  const patientDelta = changeSummary
    ? changeSummary.currentTotalPatients - changeSummary.serverTotalPatients
    : 0;

  const addedEntries = changeSummary ? Object.entries(changeSummary.treatmentPlansAdded) : [];
  const deletedEntries = changeSummary ? Object.entries(changeSummary.treatmentPlansDeleted) : [];
  const renamedEntries = changeSummary?.treatmentPlansRenamed ?? [];
  const totalAdded = addedEntries.reduce((sum, [, count]) => sum + count, 0);
  const totalDeleted = deletedEntries.reduce((sum, [, count]) => sum + count, 0);
  const treatmentTypeDelta = totalAdded - totalDeleted;

  const patientPercent = changeSummary
    ? percentChange(patientDelta, changeSummary.serverTotalPatients)
    : null;

  const planPercent = changeSummary
    ? percentChange(treatmentTypeDelta, changeSummary.serverTotalPlans)
    : null;

  const renderPercent = (percent: number | null): string =>
    percent === null ? t('save-modal-change-not-applicable') : formatPercent(percent);

  return (
    <Modal
      title={t('save-modal-title')}
      onClose={onClose}
      footer={
        <div className={toolbarStyles.modalFooter}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={onConfirm}>
            {t('save-modal-confirm')}
          </Button>
        </div>
      }
    >
      <div className={toolbarStyles.warningRow}>
        <WarningIcon className={toolbarStyles.warningIcon} />
        <span>{t('save-modal-algorithm-warning')}</span>
      </div>
      {changeSummary && (
        <div className={cardStyles.summaryRow}>
          {/* Economy card removed (REH-221): figures were hard-coded placeholders, not real data. */}
          <div className={cardStyles.summaryCard}>
            <div className={cardStyles.summaryCardHeader}>
              <div className={cardStyles.summaryCardTitle}>{t('save-modal-patients-title')}</div>
            </div>
            <div className={cardStyles.summaryCardBody}>
              <div className={cardStyles.summaryCardValue}>{formatDelta(patientDelta)}</div>
              <div className={cardStyles.summaryCardDetail}>
                {t('save-modal-patients-detail', {
                  from: changeSummary.serverTotalPatients,
                  to: changeSummary.currentTotalPatients
                })}
              </div>
              <div
                className={`${cardStyles.summaryCardFootnote} ${getFootnoteClass(patientPercent)}`}
              >
                <span className={cardStyles.summaryCardFootnoteLabel}>
                  {t('save-modal-change-label')}
                </span>
                <span className={cardStyles.summaryCardFootnoteValue}>
                  {renderPercent(patientPercent)}
                </span>
              </div>
            </div>
          </div>

          <div className={cardStyles.summaryCard}>
            <div className={cardStyles.summaryCardHeader}>
              <div className={cardStyles.summaryCardTitle}>
                {t('save-modal-treatment-types-title')}
              </div>
            </div>
            <div className={cardStyles.summaryCardBody}>
              <div className={cardStyles.summaryCardValue}>{formatDelta(treatmentTypeDelta)}</div>
              {addedEntries.map(([name, count]) => (
                <div key={`added-${name}`} className={cardStyles.summaryCardDetailRow}>
                  <span>{name}</span>
                  <span className={cardStyles.summaryCardDetailValue}>{count}</span>
                </div>
              ))}
              {deletedEntries.map(([name, count]) => (
                <div key={`deleted-${name}`} className={cardStyles.summaryCardDetailRow}>
                  <span>{name}</span>
                  <span className={cardStyles.summaryCardDetailValue}>-{count}</span>
                </div>
              ))}
              {/* A rename changes no counts, so without this line the save review
                  would show a dirty plan it cannot explain. */}
              {renamedEntries.map((renamed) => (
                <div
                  key={`renamed-${renamed.from}-${renamed.to}`}
                  className={cardStyles.summaryCardDetailRow}
                >
                  <span>{t('save-modal-renamed', { from: renamed.from, to: renamed.to })}</span>
                  <span className={cardStyles.summaryCardDetailValue}>{renamed.count}</span>
                </div>
              ))}
              {/* Same reason as the rename line: a reorder moves no counts, so
                  without this the review would show a dirty plan and nothing to
                  account for it. */}
              {changeSummary.hasReorderedTreatmentPlans && (
                <div className={cardStyles.summaryCardDetailRow}>
                  <span>{t('save-modal-rows-reordered')}</span>
                </div>
              )}
              <div className={`${cardStyles.summaryCardFootnote} ${getFootnoteClass(planPercent)}`}>
                <span className={cardStyles.summaryCardFootnoteLabel}>
                  {t('save-modal-change-label')}
                </span>
                <span className={cardStyles.summaryCardFootnoteValue}>
                  {renderPercent(planPercent)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
